import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import type { NotificationItem, NotificationList } from "@school-sales-ai/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useMarkNotificationsSeen,
  useNotifications,
  useServiceWorkerRefetch,
  useWebPush,
  type WebPushState,
} from "@/hooks/useNotifications";
import { timeAgo } from "@/lib/time-ago";
import { cn } from "@/lib/utils";

/**
 * The ONE place a notification becomes a toast. The first answer only primes
 * the set of ids already known — what was there before the page opened is in
 * the bell, not news — and afterwards every unseen id not met before is toasted
 * once. Ids, not timestamps, so a client clock that disagrees with the server
 * can neither swallow a toast nor repeat one.
 */
function useNotificationToasts(data: NotificationList | undefined, open: (path: string) => void): void {
  const known = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!data) return;
    if (known.current === null) {
      known.current = new Set(data.items.map((n) => n.id));
      return;
    }
    const fresh = data.items.filter((n) => n.seenAt === null && !known.current?.has(n.id));
    for (const n of fresh) {
      known.current.add(n.id);
      toast(n.title, {
        description: n.body,
        action: { label: "Abrir", onClick: () => open(n.path) },
      });
    }
  }, [data, open]);
}

function PushFooter({
  state,
  busy,
  onEnable,
  onDisable,
}: {
  state: WebPushState;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
}): React.JSX.Element | null {
  if (state === "unavailable") return null;
  const text: Record<Exclude<WebPushState, "unavailable">, string> = {
    unsupported:
      "Este navegador no recibe notificaciones. En iPhone, agrega el CRM a la pantalla de inicio.",
    denied: "Bloqueaste las notificaciones de este sitio. Actívalas en la configuración del navegador.",
    off: "Recibe avisos aunque el CRM esté cerrado.",
    on: "Recibes avisos en este navegador.",
  };
  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-3 py-2.5">
      <p className="text-[11.5px] leading-snug text-ink-500">{text[state]}</p>
      {state === "off" && (
        <button
          type="button"
          disabled={busy}
          onClick={onEnable}
          className="shrink-0 rounded-md bg-ink-900 px-2.5 py-1 text-[11.5px] font-semibold text-white disabled:opacity-50"
        >
          Activar
        </button>
      )}
      {state === "on" && (
        <button
          type="button"
          disabled={busy}
          onClick={onDisable}
          className="shrink-0 text-[11.5px] font-medium text-ink-500 underline-offset-2 hover:underline disabled:opacity-50"
        >
          Desactivar
        </button>
      )}
    </div>
  );
}

function NotificationRow({
  item,
  highlighted,
  onSelect,
}: {
  item: NotificationItem;
  highlighted: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className={cn("items-start gap-2.5 px-3 py-2.5", highlighted && "bg-red-50")}
      data-unseen={highlighted ? "true" : undefined}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
          highlighted ? "bg-red-500" : "bg-transparent",
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-[13px] text-ink-900",
            highlighted ? "font-semibold" : "font-medium",
          )}
        >
          {item.title}
        </span>
        <span className="block truncate text-[12px] text-ink-500">{item.body}</span>
      </span>
      <span className="shrink-0 pt-0.5 text-[11px] text-ink-500">{timeAgo(item.createdAt)}</span>
    </DropdownMenuItem>
  );
}

/**
 * The bell at the top right: unseen counter, the 30 most recent notifications
 * and the switch for this browser's push.
 *
 * Opening the panel marks everything it shows as seen (up to the newest item,
 * never past it), but the rows that were unseen WHEN IT OPENED keep their
 * highlight until it closes — clearing it the instant the panel appears would
 * hide exactly what the person opened it to find.
 */
export function NotificationBell(): React.JSX.Element {
  const navigate = useNavigate();
  const { data } = useNotifications(true);
  const markSeen = useMarkNotificationsSeen();
  const push = useWebPush(true);
  const [highlight, setHighlight] = useState<ReadonlySet<string>>(new Set());
  useServiceWorkerRefetch(true);
  useNotificationToasts(data, navigate);

  const unseen = data?.unseenCount ?? 0;
  const items = data?.items ?? [];

  function onOpenChange(open: boolean): void {
    if (!open) return;
    setHighlight(new Set(items.filter((n) => n.seenAt === null).map((n) => n.id)));
    const newest = items[0];
    if (unseen > 0 && newest) markSeen.mutate(newest.createdAt);
  }

  const BellIcon = push.state === "on" ? BellRing : push.state === "denied" ? BellOff : Bell;

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger
        aria-label={unseen > 0 ? `Notificaciones, ${unseen} sin ver` : "Notificaciones"}
        className="relative rounded-md p-1.5 text-ink-700 transition-colors hover:bg-ink-050"
      >
        <BellIcon size={18} />
        {unseen > 0 && (
          <span className="absolute -right-0.5 -top-0.5 min-w-[17px] rounded-full bg-red-500 px-1 text-center text-[10.5px] font-bold leading-[17px] text-white">
            {unseen > 99 ? "99+" : unseen}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(360px,calc(100vw-32px))] p-0">
        <div className="border-b border-ink-100 px-3 py-2.5 text-[13px] font-semibold text-ink-900">
          Notificaciones
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-1">
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-[12.5px] text-ink-500">
              Aún no tienes notificaciones.
            </p>
          ) : (
            items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                highlighted={highlight.has(item.id)}
                onSelect={() => navigate(item.path)}
              />
            ))
          )}
        </div>
        <PushFooter
          state={push.state}
          busy={push.busy}
          onEnable={() => void push.enable()}
          onDisable={() => void push.disable()}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
